import { Button, Col, Row, Tag, theme } from '@/components';
import { useCommentStackSiderView } from '@/features/comment/hooks/stack-sider-view';
import { FilterFilled, FilterOutlined } from '@ant-design/icons';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { CommentFilterParams } from '../../../models/search';
import { PinpointDisplayText } from '../../pinpoint/PinpointDisplayText';
import { CommentFilterTag } from './CommentFilterTag';

type CommentFilterParamsKey = keyof CommentFilterParams;
type CommentFilterParamsValue = CommentFilterParams[CommentFilterParamsKey];

type Props = {
  commentsFilterParams: CommentFilterParams;
  onRemoveFilter: (key: CommentFilterParamsKey) => void;
};

export const CommentSiderListFilter: FC<Props> = ({
  commentsFilterParams,
  onRemoveFilter,
}) => {
  const { t } = useTranslation();

  const stackSiderView = useCommentStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'comment-list', t('unreachable-code'));

  const { token } = theme.useToken();

  return (
    <Row gutter={[8, 0]} align="top" wrap={false}>
      <Col flex="none">
        <Button
          onClick={() => {
            stackSiderView.push({
              payload: {
                config: {
                  viewType: 'comment-filter',
                  target: siderConfig.target,
                  pinpoint: siderConfig.pinpoint,
                  pinpointKey: siderConfig.pinpointKey,
                },
              },
              target: stackSiderView.target,
            });
          }}
          icon={
            Object.values(commentsFilterParams).every(
              (value) =>
                value === null || value === undefined || value === false,
            ) && !siderConfig.pinpointKey ? (
              <FilterOutlined />
            ) : (
              <FilterFilled
                style={{
                  color: token.colorPrimary,
                }}
              />
            )
          }
        />
      </Col>
      <Col
        flex="auto"
        style={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '2px',
        }}
      >
        {(
          Object.entries(commentsFilterParams) as Array<
            [CommentFilterParamsKey, CommentFilterParamsValue]
          >
        ).map(([key, value]) => (
          <CommentFilterTag
            key={key}
            filterKey={key}
            value={value}
            onRemoveFilter={onRemoveFilter}
          />
        ))}
        {siderConfig.pinpoint && (
          <Tag closable onClose={() => onRemoveFilter('pinpointKey')}>
            <PinpointDisplayText
              whiteSpace="wrap"
              pinpoint={siderConfig.pinpoint}
            />
          </Tag>
        )}
      </Col>
    </Row>
  );
};
